"use client";
export const dynamic = "force-dynamic";

import Link from "next/link";
import { motion } from "framer-motion";
import {
  Inbox,
  Instagram,
  Send,
  Globe,
  MessageSquare,
  Users,
  Clock,
  Shuffle,
  Tag,
  Search,
  Filter,
  Bell,
  Headphones,
  Languages,
  GitBranch,
  ArrowLeft,
  PlayCircle,
  BarChart3,
  Smartphone,
  ShieldCheck,
  Zap,
  FileText,
} from "lucide-react";
import SiteHeader from "@/components/landing/SiteHeader";
import SiteFooter from "@/components/landing/SiteFooter";
import {
  SolutionShell,
  Orbs,
  GridBg,
  Section,
  SectionHeader,
  GiantStats,
  LineFeatures,
  SplitFeature,
  StepsTimeline,
  CompareSplit,
  BigQuote,
  MiniTestimonials,
  FAQ,
  FinalCTA,
  HeroBadge,
  HeroTitle,
  PricingStrip,
  Marquee,
} from "@/components/landing/solutions/SolutionSections";

const channels = [
  { name: "Instagram", icon: Instagram },
  { name: "Telegram", icon: Send },
  { name: "WhatsApp", icon: MessageSquare },
  { name: "Website Chat", icon: Globe },
  { name: "SMS", icon: Smartphone },
  { name: "Bale", icon: MessageSquare },
];

function Page() {
  return (
    <SolutionShell>
      <SiteHeader hashMode={false} />

      <section className="relative min-h-screen flex items-center pt-32 pb-20 overflow-hidden">
        <GridBg />
        <Orbs className="inset-0" />
        <div className="container mx-auto px-6 max-w-7xl relative grid lg:grid-cols-[1fr_1fr] gap-14 items-center">
          <div className="text-center lg:text-right">
            <HeroBadge>Omnichannel · همه‌ی پیام‌ها در یک‌جا</HeroBadge>
            <HeroTitle pre="همه‌ی پیام‌ها" gradient="یک inbox مشترک" />
            <p className="text-base md:text-lg text-muted-foreground max-w-xl mx-auto lg:mx-0 mb-10 leading-relaxed">
              تجربهٔ واحد برای اپراتور، دید ۳۶۰° از مشتری، تخصیص هوشمند و SLA حرفه‌ای — همه‌ی
              کانال‌ها زیر یک سقف.
            </p>
            <div className="flex flex-col sm:flex-row items-center lg:items-start justify-center lg:justify-start gap-4 mb-10">
              <Link
                href="/auth"
                className="group w-full sm:w-auto px-8 py-4 rounded-full bg-foreground text-background font-bold flex items-center justify-center gap-2 shadow-2xl shadow-primary-main/20"
              >
                شروع رایگان <ArrowLeft size={18} />
              </Link>
              <a
                href="#mockup"
                className="group flex items-center gap-3 text-foreground/75 hover:text-foreground"
              >
                <span className="w-12 h-12 rounded-full bg-foreground/5 border border-border grid place-items-center">
                  <PlayCircle size={18} />
                </span>
                <span className="text-sm font-medium">دموی inbox</span>
              </a>
            </div>
            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-x-7 gap-y-3 text-xs text-muted-foreground/80">
              <span className="flex items-center gap-1.5">
                <ShieldCheck size={13} className="text-primary-main" /> داده‌ها در ایران
              </span>
              <span className="flex items-center gap-1.5">
                <Zap size={13} className="text-secondary-main" /> سینک Real-time
              </span>
            </div>
          </div>
          <div id="mockup">
            <InboxMock />
          </div>
        </div>
      </section>

      <Marquee items={channels} />

      <GiantStats
        items={[
          { value: "۶+", label: "کانال یکپارچه" },
          { value: "%۹۲", label: "کاهش زمان پاسخ" },
          { value: "۳۶۰°", label: "دید کامل مشتری" },
          { value: "<۱s", label: "سینک بین کانال‌ها" },
        ]}
      />

      <Section>
        <SectionHeader eyebrow="ویژگی‌ها" title="یک inbox، تجربهٔ یکپارچه" />
        <LineFeatures
          items={[
            {
              icon: Users,
              title: "پروفایل ۳۶۰°",
              desc: "تاریخچهٔ همه‌ی کانال‌ها در یک پروفایل کامل مشتری.",
            },
            {
              icon: Shuffle,
              title: "تخصیص هوشمند",
              desc: "بر اساس مهارت، زبان و در دسترس بودن اپراتور.",
            },
            {
              icon: Clock,
              title: "SLA و تایمر زنده",
              desc: "هشدار خودکار وقتی پاسخ از حد قراردادی رد شد.",
            },
            {
              icon: Tag,
              title: "تگ، دسته‌بندی، ذخیره",
              desc: "گفتگوها رو سازماندهی کن و فیلترهای ذخیره‌شده بساز.",
            },
            {
              icon: FileText,
              title: "پاسخ آماده / Snippet",
              desc: "تمپلیت متنی و رسانه‌ای برای پاسخ سریع تیم.",
            },
            { icon: Bell, title: "نوتیفیکیشن هوشمند", desc: "فقط وقتی مهمه؛ بدون نویز." },
            {
              icon: Search,
              title: "جستجوی یکپارچه",
              desc: "متن، فایل یا مشتری رو بین همه‌ی کانال‌ها پیدا کن.",
            },
            {
              icon: Languages,
              title: "ترجمهٔ فوری",
              desc: "گفتگو با مشتری خارجی بدون نگرانی زبان.",
            },
          ]}
        />
      </Section>

      <Section className="border-t border-border">
        <SplitFeature
          eyebrow="پروفایل ۳۶۰"
          title={
            <>
              یک <span className="brand-gradient-text">پروفایل</span>، همه‌ی تاریخچه
            </>
          }
          desc="هرجا که مشتری پیام داده — از اینستا تا تلگرام تا وب‌سایت — همه‌ی پیام‌هاش زیر یک پروفایل."
          bullets={["تاریخچه کامل خرید و تماس", "تگ و یادداشت تیمی", "اتصال به CRM فارسی"]}
          slot={<MockProfile />}
        />
      </Section>

      <Section className="border-t border-border">
        <SplitFeature
          reverse
          eyebrow="تخصیص هوشمند"
          title={
            <>
              پیام درست به <span className="brand-gradient-text">اپراتور درست</span>
            </>
          }
          desc="با قواعد سفارشی پیام‌ها رو بر اساس مهارت، زبان و VIP بودن مشتری روتینگ کن."
          bullets={["VIP → مدیر فروش", "زبان → اپراتور هم‌زبان", "Round-robin برای پشتیبانی عمومی"]}
          slot={<MockRouting />}
        />
      </Section>

      <Section className="border-t border-border">
        <SectionHeader eyebrow="در ۴ گام" title="از کانال‌های پراکنده تا یک inbox" />
        <StepsTimeline
          steps={[
            { title: "اتصال کانال‌ها", desc: "اینستا، تلگرام، واتس‌اپ، وب." },
            { title: "تعریف تیم", desc: "نقش، شیفت و سطح دسترسی." },
            { title: "قوانین تخصیص", desc: "بر اساس کانال، تگ، VIP." },
            { title: "گزارش و بهبود", desc: "KPI تیم و بهینه‌سازی SLA." },
          ]}
        />
      </Section>

      <Section className="border-t border-border">
        <SectionHeader eyebrow="مقایسه" title="ترندی در برابر چند ابزار جدا" accent="secondary" />
        <CompareSplit
          oursLabel="ترندی Omnichannel"
          theirsLabel="چند نرم‌افزار جدا"
          ours={[
            "یک پروفایل واحد مشتری",
            "گزارش متمرکز چندکاناله",
            "یک پلن، یک قیمت",
            "یک محیط برای آموزش تیم",
            "اتصال بومی به CRM فارسی",
          ]}
          theirs={[
            "اطلاعات تکراری در هر ابزار",
            "گزارش‌های ناهماهنگ",
            "۳ تا ۵ اشتراک جداگانه",
            "هر ابزار یک رابط متفاوت",
            "ادغام دستی و دردسر",
          ]}
        />
      </Section>

      <Section>
        <BigQuote
          quote="از ۴ نرم‌افزار به یک پنل کوچ کردیم؛ زمان پاسخ نصف شد و گزارش‌های چندکاناله نقطه‌عطف ما بود."
          name="مهدی کریمی"
          role="مدیر پشتیبانی · دیجی‌مال"
        />
      </Section>

      <Section className="border-t border-border">
        <SectionHeader eyebrow="نظر مشتریان" title="تیم‌هایی که نفس راحت کشیدند" />
        <MiniTestimonials
          items={[
            {
              name: "ریحانه فلاح",
              role: "Head of CX · فلوریا",
              quote: "گزارش‌های چندکاناله ترندی نقطه عطف ما بود.",
            },
            {
              name: "بهنام شریفی",
              role: "آژانس آرکا",
              quote: "برای ۱۲ برند مشتری از یک پنل کار می‌کنیم.",
            },
            {
              name: "Niloofar A.",
              role: "E-commerce",
              quote: "Trendi unified our 5 channels into one.",
            },
          ]}
        />
      </Section>

      <Section>
        <SectionHeader eyebrow="پرسش‌ها" title="سؤالات رایج" />
        <FAQ
          items={[
            {
              q: "آیا پیام‌های قدیمی هم منتقل می‌شوند؟",
              a: "بله، تا ۹۰ روز اخیر را خودکار سینک می‌کنیم.",
            },
            { q: "محدودیت کانال چقدر است؟", a: "در پلن Pro تا ۱۰ کانال و در Enterprise نامحدود." },
            { q: "آیا API دارد؟", a: "بله، REST و Webhook برای اتصال به ERP و فروشگاه." },
          ]}
        />
      </Section>

      <PricingStrip />
      <FinalCTA
        title={
          <>
            یک inbox، <span className="brand-gradient-text">صفر</span> آشفتگی
          </>
        }
        desc="کانال‌های پراکنده‌ت رو امروز وصل کن."
      />
      <SiteFooter />
    </SolutionShell>
  );
}

function InboxMock() {
  const items = [
    { name: "ساره ر.", ch: Instagram, msg: "قیمت کیف چرم؟", t: "۲ دق" },
    { name: "علی م.", ch: Send, msg: "سفارش ۱۲۰۹ کجاست؟", t: "۵ دق" },
    { name: "نگار ف.", ch: MessageSquare, msg: "می‌خوام مرجوع کنم", t: "۷ دق" },
    { name: "Behzad S.", ch: Globe, msg: "Refund policy?", t: "۱۲ دق" },
  ];
  return (
    <motion.div
      initial={{ opacity: 0, x: 30 }}
      animate={{ opacity: 1, x: 0 }}
      className="relative rounded-3xl border border-border bg-background shadow-2xl overflow-hidden"
    >
      <div className="absolute -inset-6 rounded-[3rem] bg-gradient-to-br from-primary-main/30 to-secondary-main/30 blur-3xl opacity-50 -z-10" />
      <div className="px-5 py-3 border-b border-border flex items-center justify-between bg-foreground/[0.05]">
        <div className="flex items-center gap-2 text-sm font-black text-foreground">
          <Inbox size={16} className="text-primary-main" /> Inbox مشترک
        </div>
        <div className="text-[10px] text-muted-foreground">۴ کانال · ۲۸ پیام</div>
      </div>
      <div className="grid grid-cols-[1fr_1.4fr] min-h-[380px]">
        <div className="border-l border-border">
          {items.map((it, i) => (
            <div
              key={i}
              className={`flex gap-3 px-4 py-3 border-b border-border ${i === 0 ? "bg-primary-main/10" : ""}`}
            >
              <div className="w-9 h-9 rounded-full bg-foreground/[0.08] grid place-items-center">
                <it.ch size={14} className="text-foreground/75" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-black text-foreground">{it.name}</span>
                  <span className="text-[10px] text-muted-foreground/80">{it.t}</span>
                </div>
                <div className="text-[11px] text-muted-foreground truncate">{it.msg}</div>
              </div>
            </div>
          ))}
        </div>
        <div className="p-5 space-y-3">
          <div className="text-[10px] text-muted-foreground/80">گفتگو با ساره ر. — Instagram</div>
          <div className="bg-foreground/[0.08] text-foreground text-xs px-3 py-2 rounded-xl max-w-[80%]">
            قیمت کیف چرمی چقدره؟
          </div>
          <div className="bg-primary-main/15 border border-primary-main/30 text-foreground text-xs px-3 py-2 rounded-xl max-w-[80%] mr-auto">
            ۱.۲۸۰.۰۰۰ تومن، الان ۱۵٪ تخفیف داره 🌸
          </div>
          <div className="flex items-center gap-2 pt-2">
            <input
              className="flex-1 px-3 py-2 rounded-xl border border-border bg-foreground/[0.05] text-xs text-foreground"
              placeholder="پاسخ بنویس…"
              readOnly
            />
            <button className="w-9 h-9 rounded-xl bg-primary-main text-foreground grid place-items-center">
              <Send size={14} />
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function MockProfile() {
  return (
    <div className="relative rounded-3xl border border-border bg-foreground/[0.04] p-6">
      <div className="flex items-center gap-3 pb-4 border-b border-border">
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary-main to-secondary-main" />
        <div>
          <div className="font-black text-foreground">ساره رستمی</div>
          <div className="text-xs text-muted-foreground">VIP · ۱۲ خرید · ۲۸M تومان</div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 my-4">
        {[Instagram, Send, MessageSquare, Globe].map((I, i) => (
          <div
            key={i}
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-foreground/[0.04] border border-border/60"
          >
            <I size={14} className="text-primary-main" />
            <span className="text-[11px] text-foreground">۱۲ پیام</span>
          </div>
        ))}
      </div>
      <div className="space-y-2">
        {["سفارش ۱۲۹۰ – ارسال شد", "تماس پشتیبانی – حل شد", "خرید Reels Bag – تأیید"].map((e) => (
          <div key={e} className="flex items-center gap-2 text-[11px] text-foreground/75">
            <span className="w-1.5 h-1.5 rounded-full bg-primary-main" />
            {e}
          </div>
        ))}
      </div>
    </div>
  );
}

function MockRouting() {
  return (
    <div className="relative rounded-3xl border border-border bg-foreground/[0.04] p-6">
      <div className="flex items-center gap-2 text-sm font-black text-foreground mb-4">
        <GitBranch size={16} className="text-secondary-main" /> قواعد روتینگ
      </div>
      {[
        { c: "if VIP", a: "→ مدیر فروش", t: "primary" },
        { c: "if زبان=EN", a: "→ تیم بین‌الملل", t: "secondary" },
        { c: "if شکایت", a: "→ پشتیبانی ارشد", t: "primary" },
        { c: "else", a: "→ Round-robin", t: "secondary" },
      ].map((r, i) => (
        <div
          key={i}
          className="flex items-center gap-3 py-2.5 border-b border-border/60 last:border-0"
        >
          <span
            className={`text-xs font-black px-2 py-0.5 rounded-md ${r.t === "primary" ? "bg-primary-main/15 text-primary-main" : "bg-secondary-main/15 text-secondary-main"}`}
          >
            {r.c}
          </span>
          <span className="text-sm text-foreground/90">{r.a}</span>
          <Headphones size={12} className="text-muted-foreground/80 mr-auto" />
        </div>
      ))}
      <div className="mt-4 pt-3 border-t border-border flex items-center justify-between text-[11px] text-muted-foreground">
        <span>۴ قانون فعال</span>
        <span className="text-primary-main flex items-center gap-1">
          <BarChart3 size={11} /> %۹۸ موفق
        </span>
      </div>
    </div>
  );
}

export default Page;
